import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { describeApiError } from '@/api/problemDetails'
import { Alert, Button, toast } from '@/components/ui'
import { cn } from '@/lib/utils'

import { useAddInternalNote, useReplyToConversation } from './queries'

type ComposerMode = 'reply' | 'note'

interface ReplyComposerProps {
  readonly tenantId: string
  readonly conversationId: number
}

/**
 * Compositor com dois modos explicitos: RESPONDER (mensagem publica ao cliente — acao humana) e NOTA INTERNA
 * (privada, nunca entregue ao cliente). A distincao publica/privada e sempre explicita para o operador (classificacao;
 * GL-0019). O agente nunca envia publico — isto e o caminho humano.
 */
export function ReplyComposer({ tenantId, conversationId }: ReplyComposerProps) {
  const { t } = useTranslation()
  const textareaId = useId()
  const [mode, setMode] = useState<ComposerMode>('reply')
  const [content, setContent] = useState('')

  const reply = useReplyToConversation(tenantId, conversationId)
  const note = useAddInternalNote(tenantId, conversationId)

  const active = mode === 'reply' ? reply : note
  const trimmed = content.trim()
  const errors = describeApiError(active.error)

  function submit() {
    if (trimmed.length === 0 || active.isPending) {
      return
    }

    active.mutate(trimmed, {
      onSuccess: () => {
        setContent('')
        toast.success(
          mode === 'reply' ? t('desk.composer.replySent') : t('desk.composer.noteSaved'),
        )
      },
    })
  }

  return (
    <div className="flex shrink-0 flex-col gap-2 border-t border-border-default p-3">
      <div role="tablist" aria-label={t('desk.composer.modeLabel')} className="flex gap-1">
        {(['reply', 'note'] as const).map((value) => (
          <button
            key={value}
            type="button"
            role="tab"
            aria-selected={mode === value}
            onClick={() => {
              setMode(value)
            }}
            className={cn(
              'rounded-md px-2.5 py-1 text-xs font-medium',
              mode === value
                ? 'bg-background-subtle text-foreground-primary'
                : 'text-foreground-muted hover:text-foreground-secondary',
            )}
          >
            {value === 'reply' ? t('desk.composer.reply') : t('desk.composer.note')}
          </button>
        ))}
      </div>

      {mode === 'note' ? (
        <p className="text-xs text-foreground-muted">{t('desk.composer.noteHint')}</p>
      ) : null}

      {errors.length > 0 ? (
        <Alert variant="danger" title={t('desk.composer.sendFailed')}>
          <ul className="list-disc pl-4">
            {errors.map((message) => (
              <li key={message}>{message}</li>
            ))}
          </ul>
        </Alert>
      ) : null}

      <label htmlFor={textareaId} className="sr-only">
        {mode === 'reply' ? t('desk.composer.reply') : t('desk.composer.note')}
      </label>
      <textarea
        id={textareaId}
        value={content}
        onChange={(event) => {
          setContent(event.target.value)
        }}
        onKeyDown={(event) => {
          // Enviar com Ctrl/Cmd + Enter (docs/design/07 secao 5: keyboard shortcut).
          if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
            event.preventDefault()
            submit()
          }
        }}
        rows={3}
        maxLength={4000}
        placeholder={
          mode === 'reply'
            ? t('desk.composer.replyPlaceholder')
            : t('desk.composer.notePlaceholder')
        }
        className={cn(
          'w-full resize-y rounded-md border bg-background-elevated px-3 py-2 text-sm text-foreground-primary placeholder:text-foreground-muted',
          'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus-ring',
          mode === 'note' ? 'border-status-warning/50' : 'border-border-default',
        )}
      />

      <div className="flex items-center justify-end">
        <Button
          variant={mode === 'reply' ? 'primary' : 'secondary'}
          size="sm"
          loading={active.isPending}
          disabled={trimmed.length === 0}
          onClick={submit}
        >
          {mode === 'reply' ? t('desk.composer.send') : t('desk.composer.saveNote')}
        </Button>
      </div>
    </div>
  )
}
